import {
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Link,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
  Icon,
  useToast,
} from '@chakra-ui/react';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import { MdSearch, MdClear, MdFileDownload } from 'react-icons/md';
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  ApplicationPathName,
  ApproveStatus,
  EquipmentCategory,
  EquipmentUsageType,
  PurposeType,
  getStatusNameByCode,
  getEquipmentCategoryNameByCode,
  getEquipmentCategoryNamesByCodes,
  getEquipmentUsageTypeNameByCode,
  getPurposeTypeNameByCode,
} from '../../contexts/constants';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { OrganizationWithHierarchyType } from '../../contexts/types';
import * as XLSX from 'xlsx';
import ParallelApprovalIcons from '../../components/ParallelApprovalIcons';
import { hasFilterString, formatMultiValue, openInvestmentScreeningDetailInNewTab } from '../../utils/utils';
import TruncatedCell from '../../components/TruncatedCell';
import DebouncedInput from '../../components/DebouncedInput';
import { getHeadquartersNameFromHierarchy, getDivisionNameFromHierarchy } from '../../utils/i18nDisplay';

type SortConfig = { key: string; ascending: boolean };

/** 列リサイズハンドル */
const ResizeHandle: React.FC<{ colIndex: number; setColWidths: React.Dispatch<React.SetStateAction<number[]>> }> = ({ colIndex, setColWidths }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const th = (e.target as HTMLElement).closest("th");
    if (!th) return;
    const table = th.closest("table");
    if (!table) return;
    const ths = Array.from(table.querySelectorAll("thead th"));
    const startWidth = th.getBoundingClientRect().width;
    setColWidths(ths.map(t => t.getBoundingClientRect().width));
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(50, startWidth + delta);
        return next;
      });
    };
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  return (
    <Box
      position="absolute"
      right="-2px"
      top={0}
      bottom={0}
      width="5px"
      cursor="col-resize"
      onMouseDown={handleMouseDown}
      _hover={{ bg: "blue.300" }}
      zIndex={2}
    />
  );
};

/**
 * 事業管理一覧画面
 */
const ManagementList: React.FC = () => {

  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  // -----------------------------
  // useState
  // -----------------------------
  // （検索条件）適用年月
  const [conditionFiscalPeriod, setConditionFiscalPeriod] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });
  // （検索条件）事業本部
  const [conditionHeadquarters, setConditionHeadquarters] = useState<string>("");
  // （検索条件）事業部
  const [conditionDivisionOrg, setConditionDivisionOrg] = useState<string>("");
  // （検索条件）申請月度
  const [conditionApplicationMonth, setConditionApplicationMonth] = useState<string>("");
  // （検索条件）Status
  const [conditionStatus, setConditionStatus] = useState<string>("");

  // （テーブルフィルター）
  const [filterProjectName, setFilterProjectName] = useState<string>("");
  const [filterHeadquarters, setFilterHeadquarters] = useState<string>("");
  const [filterDivision, setFilterDivision] = useState<string>("");
  const [filterApplicationMonth, setFilterApplicationMonth] = useState<string>("");
  const [filterEquipmentCategory, setFilterEquipmentCategory] = useState<string>("");
  const [filterPurpose, setFilterPurpose] = useState<string>("");
  const [filterEquipmentUsageType, setFilterEquipmentUsageType] = useState<string>("");
  const [filterApplicationNumber, setFilterApplicationNumber] = useState<string>("");
  const [filterEquipmentPlanNumber, setFilterEquipmentPlanNumber] = useState<string>("");
  const [filterApplicationAmount, setFilterApplicationAmount] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  // ソート
  const [sort, setSort] = useState<SortConfig>({ key: "applicationMonth", ascending: false });
  // 列幅リサイズ用
  const [colWidths, setColWidths] = useState<number[]>([]);
  // 経理組織情報
  const [accountingOrganizations, setAccountingOrganizations] = useState<OrganizationWithHierarchyType[]>([]);
  // 事業管理一覧データ
  const [managementTargets, setManagementTargets] = useState<any[]>([]);

  // ソートボタン描画
  const renderSortButton = (column: string) => (
    <IconButton
      h="16px"
      onClick={() => setSort(sort.key === column ? { key: column, ascending: !sort.ascending } : { key: column, ascending: true })}
      _hover={{ bg: "gray.100", cursor: "pointer" }}
      icon={sort.key === column ? (sort.ascending ? <FaSortAmountUp /> : <FaSortAmountDown />) : <FaSort />}
      aria-label={t('common.label.sort')}
    />
  );

  // ソート値取得
  const getSortValue = (item: any, key: string): string => {
    switch (key) {
      case "projectName": return item.projectName || "";
      case "headquarters": return item.headquartersName || "";
      case "division": return item.divisionName || "";
      case "applicationMonth": return item.applicationMonth || "";
      case "equipmentCategory": return item.equipmentCategoryName || "";
      case "purpose": return item.purposeName || "";
      case "equipmentUsageType": return item.equipmentUsageTypeName || "";
      case "applicationNumber": return item.applicationNumber || "";
      case "equipmentPlanNumber": return formatMultiValue(item.equipmentPlanNumber);
      case "applicationAmount": return String(item.equipmentTotalAmount || 0).padStart(15, "0");
      case "status": return item.statusName || "";
      default: return "";
    }
  };

  // -----------------------------
  // API
  // -----------------------------
  // 経理組織情報取得API
  const getAccountingOrganization = async (fiscalPeriod: string) => {
    const functionName = "getAccountingOrganization";
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query getAccountingOrganization($hasDeleted: Boolean, $fiscalPeriod: String) {
            getAccountingOrganization(hasDeleted: $hasDeleted, fiscalPeriod: $fiscalPeriod) {
              organizationId
              organizationName
              parentOrganizationId
              parentOrganizationName
              fiscalPeriod
            }
          }
        `,
        variables: {
          hasDeleted: false,
          fiscalPeriod: fiscalPeriod || undefined,
        },
      });
      console.log(`${functionName} RESPONSE:`, response);
      return response?.data?.getAccountingOrganization as OrganizationWithHierarchyType[] | undefined;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return undefined;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // プロジェクト検索API
  const getProjectSearch = async () => {
    const functionName = 'getProjectSearch';
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query getProjectSearch(
            $searchType: String!,
            $filterOrganizationIds: String
          ) {
            getProjectSearch(
              searchType: $searchType,
              filterOrganizationIds: $filterOrganizationIds
            ) {
              projectId
              projectName
              applicationNumber
              equipmentPlanNumber
              approvalStatus
              purpose
              equipmentUsageType
              divisionHierarchy
              divisionHierarchyEn
              equipmentCategory
              applicationMonth
              equipmentTotalAmount
              parallelApprovalStatus
              crossCheckRequired
              materialOfficerRequired
            }
          }
        `,
        variables: {
          searchType: 'management',
          filterOrganizationIds: buildFilterOrganizationIds(),
        }
      });
      console.log(`${functionName} RESPONSE:`, response);
      return response?.data?.getProjectSearch;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return undefined;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // -----------------------------
  // useEffect
  // -----------------------------
  useEffect(() => {
    fetchAccountingOrganizations(conditionFiscalPeriod);
  }, []);

  // -----------------------------
  // 業務ロジック
  // -----------------------------
  const fetchAccountingOrganizations = async (fiscalPeriod: string) => {
    const orgData = await getAccountingOrganization(fiscalPeriod);
    if (orgData) {
      setAccountingOrganizations(orgData);
    }
    setConditionHeadquarters("");
    setConditionDivisionOrg("");
  };

  // 事業本部変更処理
  const handleHeadquartersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setConditionHeadquarters(e.target.value);
    setConditionDivisionOrg("");
  };

  // 適用年月変更処理
  const handleFiscalPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConditionFiscalPeriod(value);
    if (value) {
      fetchAccountingOrganizations(value);
    }
  };

  // 事業部リスト（事業本部の配下を算出）
  const divisionOptions = useMemo(() => {
    if (!conditionHeadquarters) return [];
    const departments = accountingOrganizations.filter(o => o.parentOrganizationId === conditionHeadquarters);
    return departments.flatMap(dept =>
      accountingOrganizations.filter(o => o.parentOrganizationId === dept.organizationId)
    );
  }, [accountingOrganizations, conditionHeadquarters]);

  // 選択された組織の子孫IDを再帰的に収集
  const buildFilterOrganizationIds = (): string => {
    if (!conditionHeadquarters) return "";
    const rootOrgId = conditionDivisionOrg || conditionHeadquarters;
    const collectDescendantIds = (parentId: string): string[] => {
      const children = accountingOrganizations.filter(o => o.parentOrganizationId === parentId);
      return children.flatMap(c => [c.organizationId, ...collectDescendantIds(c.organizationId)]);
    };
    return [rootOrgId, ...collectDescendantIds(rootOrgId)].join(",");
  };

  // フィルタリング＆ソート
  const filteredData = useMemo(() => {
    return (managementTargets ?? [])
      .filter(item => {
        if (!item) return false;
        // 検索条件パネルのクライアントサイドフィルタ（申請月度、Status）
        if (conditionApplicationMonth && item.applicationMonth !== conditionApplicationMonth) return false;
        if (conditionStatus && item.approvalStatus !== conditionStatus) return false;
        // テーブルヘッダーフィルタ
        const headquartersName = getHeadquartersNameFromHierarchy(item);
        const divisionName = getDivisionNameFromHierarchy(item);
        return (
          hasFilterString(item.projectName, filterProjectName) &&
          hasFilterString(headquartersName, filterHeadquarters) &&
          hasFilterString(divisionName, filterDivision) &&
          hasFilterString(item.applicationMonth, filterApplicationMonth) &&
          hasFilterString(item.equipmentCategoryName, filterEquipmentCategory) &&
          hasFilterString(item.purposeName, filterPurpose) &&
          hasFilterString(item.equipmentUsageTypeName, filterEquipmentUsageType) &&
          hasFilterString(item.applicationNumber, filterApplicationNumber) &&
          hasFilterString(formatMultiValue(item.equipmentPlanNumber), filterEquipmentPlanNumber) &&
          hasFilterString(item.applicationAmountDisplay, filterApplicationAmount) &&
          hasFilterString(item.statusName, filterStatus)
        );
      })
      .sort((a, b) => {
        const valA = getSortValue(a, sort.key);
        const valB = getSortValue(b, sort.key);
        const cmp = valA.localeCompare(valB);
        return sort.ascending ? cmp : -cmp;
      });
  }, [managementTargets, conditionApplicationMonth, conditionStatus,
    filterProjectName, filterHeadquarters, filterDivision, filterApplicationMonth,
    filterEquipmentCategory, filterPurpose, filterEquipmentUsageType, filterApplicationNumber, filterEquipmentPlanNumber, filterApplicationAmount, filterStatus, sort]);

  // 検索ボタン押下処理
  const handleSearch = async () => {
    // テーブルフィルターをリセット
    setFilterProjectName("");
    setFilterHeadquarters("");
    setFilterDivision("");
    setFilterApplicationMonth("");
    setFilterEquipmentCategory("");
    setFilterPurpose("");
    setFilterEquipmentUsageType("");
    setFilterApplicationNumber("");
    setFilterEquipmentPlanNumber("");
    setFilterApplicationAmount("");
    setFilterStatus("");

    const projects = await getProjectSearch();
    if (projects) {
      const validProjects = projects.filter((p: null | undefined) => p !== null && p !== undefined);
      const amountUnit = t('features.Management.List.excel.amountUnit');
      const targetList: any[] = validProjects.map((p: any) => ({
        ...p,
        headquartersName: getHeadquartersNameFromHierarchy(p),
        divisionName: getDivisionNameFromHierarchy(p),
        equipmentCategoryName: getEquipmentCategoryNamesByCodes(p.equipmentCategory ?? ''),
        purposeName: getPurposeTypeNameByCode(p.purpose),
        equipmentUsageTypeName: getEquipmentUsageTypeNameByCode(p.equipmentUsageType || ""),
        statusName: getStatusNameByCode(p.approvalStatus ?? ''),
        applicationAmountDisplay: p.equipmentTotalAmount
          ? Number(p.equipmentTotalAmount).toLocaleString() + " " + amountUnit
          : "0 " + amountUnit,
      }));
      setManagementTargets(targetList);
      if (targetList.length === 0) {
        toast({
          title: t('common.message.searchResult'),
          description: t('common.message.noDataFound'),
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: t('common.message.searchExecutionError'),
        description: t('features.Management.List.message.searchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Excel出力処理
  const handleExcelExport = () => {
    if (filteredData.length === 0) {
      toast({
        title: t('common.button.excelExport'),
        description: t('common.message.excelExportNoData'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const headers = [
      t('features.Management.List.label.projectNameEn'),
      t('common.field.headquarters'),
      t('common.field.division'),
      t('common.field.applicationMonth'),
      t('common.field.equipmentCategory'),
      t('common.field.purpose'),
      t('features.Management.List.label.equipmentUsageType'),
      t('common.field.applicationNumber'),
      t('common.field.equipmentPlanNumber'),
      t('common.field.applicationAmount'),
      t('common.field.status'),
    ];

    const rows = filteredData.map((item: any) => [
      item.projectName || '',
      item.headquartersName || '',
      item.divisionName || '',
      item.applicationMonth || '',
      item.equipmentCategoryName || '',
      item.purposeName || '',
      item.equipmentUsageTypeName || '',
      item.applicationNumber || '',
      formatMultiValue(item.equipmentPlanNumber),
      item.applicationAmountDisplay || '',
      item.statusName || '',
    ]);

    const sheetData = [headers, ...rows];
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(sheetData);

    // 列幅自動調整（日本語対応）
    const colWidthValues: number[] = [];
    for (const row of sheetData) {
      row.forEach((cell, i) => {
        const cellValue = String(cell ?? '');
        const cellLength = [...cellValue].reduce(
          (sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 2 : 1),
          0
        );
        colWidthValues[i] = Math.max(colWidthValues[i] || 10, cellLength);
      });
    }
    sheet['!cols'] = colWidthValues.map(w => ({ wch: w + 2 }));

    XLSX.utils.book_append_sheet(workbook, sheet, t('features.Management.List.excel.sheetName'));

    const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const timestamp = now.toISOString().slice(0, 19).replace(/[-:T]/g, '').replace(/(\d{8})(\d{6})/, '$1_$2');
    XLSX.writeFile(workbook, `${t('features.Management.List.excel.fileName')}_${timestamp}.xlsx`);
  };

  // 検索条件クリアボタン押下処理
  const handleClearCondition = () => {
    const today = new Date();
    const defaultFiscalPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    if (conditionFiscalPeriod !== defaultFiscalPeriod) {
      setConditionFiscalPeriod(defaultFiscalPeriod);
      fetchAccountingOrganizations(defaultFiscalPeriod);
    }
    setConditionHeadquarters("");
    setConditionDivisionOrg("");
    setConditionApplicationMonth("");
    setConditionStatus("");
  };

  return (
    <Box h="100%" display="flex" flexDirection="column" gap={4} p={4} bg="gray.50">
      {/* 検索条件パネル */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
        <Wrap spacing={4} align="flex-end">
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>{t('common.field.managementStructure')}</FormLabel>
              <Flex gap={3} align="center">
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.applicablePeriod')}</Text>
                  <Input
                    type="month"
                    size="sm"
                    value={conditionFiscalPeriod}
                    onChange={handleFiscalPeriodChange}
                  />
                </Box>
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.headquarters')}</Text>
                  <Select
                    w="200px"
                    value={conditionHeadquarters}
                    onChange={handleHeadquartersChange}
                    size="sm"
                  >
                    <option value=""></option>
                    {accountingOrganizations
                      .filter(o => o.parentOrganizationId === "organization00000")
                      .map(o => (
                        <option key={o.organizationId} value={o.organizationId}>
                          {o.organizationName}
                        </option>
                      ))}
                  </Select>
                </Box>
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.division')}</Text>
                  <Select
                    w="200px"
                    value={conditionDivisionOrg}
                    onChange={(e) => setConditionDivisionOrg(e.target.value)}
                    size="sm"
                    disabled={!conditionHeadquarters}
                  >
                    <option value=""></option>
                    {divisionOptions.map(o => (
                      <option key={o.organizationId} value={o.organizationId}>
                        {o.organizationName}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>{t('common.field.applicationMonth')}</FormLabel>
              <Input
                type="month"
                size="sm"
                w="180px"
                value={conditionApplicationMonth}
                onChange={(e) => setConditionApplicationMonth(e.target.value)}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>{t('common.field.status')}</FormLabel>
              <Select
                w="200px"
                size="sm"
                value={conditionStatus}
                onChange={(e) => setConditionStatus(e.target.value)}
              >
                <option value="">{t('common.label.all')}</option>
                {Object.values(ApproveStatus)
                  .filter(({ code }) => code !== ApproveStatus.Cancel.code && code !== ApproveStatus.Reject.code)
                  .map(({ code }) => (
                    <option key={code} value={code}>{getStatusNameByCode(code)}</option>
                  ))}
              </Select>
            </Box>
          </WrapItem>
        </Wrap>
        <Flex justify="flex-end" mt={3} gap={2}>
          <Button leftIcon={<Icon as={MdSearch} />} size="sm" colorScheme="blue" onClick={handleSearch}>
            {t('common.button.search')}
          </Button>
          <Button leftIcon={<Icon as={MdClear} />} size="sm" variant="outline" colorScheme="red" onClick={handleClearCondition}>
            {t('common.button.searchClear')}
          </Button>
        </Flex>
      </Box>

      {/* テーブル */}
      <Box flex={1} minH={0} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden" display="flex" flexDirection="column">
        <Flex px={4} py={2} borderBottom="1px solid" borderColor="gray.100" bg="white" position="relative" zIndex={2} flexShrink={0} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.600">{t('common.message.searchResult')}: <Text as="span" fontWeight="bold" color="gray.800">{filteredData.length}</Text> {t('common.label.count')}</Text>
          <Button leftIcon={<Icon as={MdFileDownload} />} size="sm" colorScheme="green" variant="outline" onClick={handleExcelExport} isDisabled={filteredData.length === 0}>
            {t('common.button.excelExport')}
          </Button>
        </Flex>
        <Box flex={1} minH={0} overflow="auto">
          <Table borderX="1px solid" borderBottom="1px solid" borderColor="gray.200" variant="simple" size="sm"
            sx={{ tableLayout: 'fixed' }}
            width={colWidths.length > 0 ? `${colWidths.reduce((a, b) => a + b, 0)}px` : "130%"}
          >
            <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
              <Tr bgColor="gray.50">
                {/* 1: Project名 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[0] ? `${colWidths[0]}px` : "18%"} position="relative">
                  {t('features.Management.List.label.projectNameEn')}{renderSortButton("projectName")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterProjectName} onChange={setFilterProjectName} />
                  <ResizeHandle colIndex={0} setColWidths={setColWidths} />
                </Th>
                {/* 2: 事業本部 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[1] ? `${colWidths[1]}px` : "14%"} position="relative">
                  {t('common.field.headquarters')}{renderSortButton("headquarters")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterHeadquarters} onChange={setFilterHeadquarters} />
                  <ResizeHandle colIndex={1} setColWidths={setColWidths} />
                </Th>
                {/* 3: 事業部 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[2] ? `${colWidths[2]}px` : "12%"} position="relative">
                  {t('common.field.division')}{renderSortButton("division")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterDivision} onChange={setFilterDivision} />
                  <ResizeHandle colIndex={2} setColWidths={setColWidths} />
                </Th>
                {/* 4: 申請月度 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[3] ? `${colWidths[3]}px` : "9%"} position="relative">
                  {t('common.field.applicationMonth')}{renderSortButton("applicationMonth")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApplicationMonth} onChange={setFilterApplicationMonth} />
                  <ResizeHandle colIndex={3} setColWidths={setColWidths} />
                </Th>
                {/* 5: 設備区分 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[4] ? `${colWidths[4]}px` : "12%"} position="relative">
                  {t('common.field.equipmentCategory')}{renderSortButton("equipmentCategory")}
                  <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterEquipmentCategory} onChange={(e) => setFilterEquipmentCategory(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(EquipmentCategory).map(({ code, name }) => <option key={code} value={name}>{getEquipmentCategoryNameByCode(code)}</option>)}
                  </Select>
                  <ResizeHandle colIndex={4} setColWidths={setColWidths} />
                </Th>
                {/* 6: 目的 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[5] ? `${colWidths[5]}px` : "8%"} position="relative">
                  {t('common.field.purpose')}{renderSortButton("purpose")}
                  <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterPurpose} onChange={(e) => setFilterPurpose(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                  </Select>
                  <ResizeHandle colIndex={5} setColWidths={setColWidths} />
                </Th>
                {/* 7: 設備用途区分 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[6] ? `${colWidths[6]}px` : "9%"} position="relative">
                  <Box as="span" whiteSpace="nowrap">{t('features.Management.List.label.equipmentUsageType')}{renderSortButton("equipmentUsageType")}</Box>
                  <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterEquipmentUsageType} onChange={(e) => setFilterEquipmentUsageType(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                  </Select>
                  <ResizeHandle colIndex={6} setColWidths={setColWidths} />
                </Th>
                {/* 8: 申請番号 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[7] ? `${colWidths[7]}px` : "11%"} position="relative">
                  {t('common.field.applicationNumber')}{renderSortButton("applicationNumber")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApplicationNumber} onChange={setFilterApplicationNumber} />
                  <ResizeHandle colIndex={7} setColWidths={setColWidths} />
                </Th>
                {/* 9: 設備計画番号 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[8] ? `${colWidths[8]}px` : "10%"} position="relative">
                  {t('common.field.equipmentPlanNumber')}{renderSortButton("equipmentPlanNumber")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterEquipmentPlanNumber} onChange={setFilterEquipmentPlanNumber} />
                  <ResizeHandle colIndex={8} setColWidths={setColWidths} />
                </Th>
                {/* 10: 申請金額 */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="right" width={colWidths[9] ? `${colWidths[9]}px` : "10%"} position="relative">
                  {t('common.field.applicationAmount')}{renderSortButton("applicationAmount")}
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApplicationAmount} onChange={setFilterApplicationAmount} />
                  <ResizeHandle colIndex={9} setColWidths={setColWidths} />
                </Th>
                {/* 11: Status */}
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[10] ? `${colWidths[10]}px` : "12%"} position="relative">
                  {t('common.field.status')}{renderSortButton("status")}
                  <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(ApproveStatus)
                      .filter(({ code }) => code !== ApproveStatus.Cancel.code && code !== ApproveStatus.Reject.code)
                      .map(({ code, name }) => <option key={code} value={name}>{getStatusNameByCode(code)}</option>)}
                  </Select>
                  <ResizeHandle colIndex={10} setColWidths={setColWidths} />
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.map((item: any) => (
                <Tr key={item.projectId} _hover={{ bg: "gray.50" }}>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.projectName || ""}>
                      <Link
                        as={RouterLink}
                        to={ApplicationPathName.InvestmentScreeningDetail.Url}
                        state={{ projectId: item.projectId }}
                        textColor={'blue.500'}
                        textDecoration={'underline'}
                        onClick={(e) => openInvestmentScreeningDetailInNewTab(e, item.projectId || "")}
                        onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, item.projectId || "")}
                      >
                        {item.projectName}
                      </Link>
                    </TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.headquartersName || ""}>{item.headquartersName}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.divisionName || ""}>{item.divisionName}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.applicationMonth || ""}>{item.applicationMonth}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.equipmentCategoryName || ""}>{item.equipmentCategoryName}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.purposeName || ""}>{item.purposeName}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.equipmentUsageTypeName || ""}>{item.equipmentUsageTypeName}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.applicationNumber || ""}>{item.applicationNumber}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={formatMultiValue(item.equipmentPlanNumber)}>{formatMultiValue(item.equipmentPlanNumber)}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    <TruncatedCell label={item.applicationAmountDisplay || ""}>{item.applicationAmountDisplay}</TruncatedCell>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    {item.parallelApprovalStatus ? (
                      <ParallelApprovalIcons
                        approvalStatus={item.approvalStatus || ""}
                        parallelApprovalStatus={item.parallelApprovalStatus}
                        crossCheckRequired={item.crossCheckRequired}
                        materialOfficerRequired={item.materialOfficerRequired}
                      />
                    ) : (
                      <TruncatedCell label={item.statusName || ""}>{item.statusName}</TruncatedCell>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default ManagementList;
